import axios from 'axios';

// ============================================
// CONFIGURATION AUTOMATIQUE DE L'ENVIRONNEMENT
// ============================================

// Détecte automatiquement l'environnement
const isProduction = window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1';

// URLs de base selon l'environnement
const API_BASE_URL = isProduction
  ? 'https://www.africannpms.org/api/v1'           // Production
  : 'http://localhost:8000/api/v1';                 // Localhost

const API_ADMIN_URL = isProduction
  ? 'https://www.africannpms.org/api/v1/admin'     // Production
  : 'http://localhost:8000/api/v1/admin';           // Localhost

// Instance Axios configurée
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  }
});

// Instance Axios pour les routes admin
const apiAdminClient = axios.create({
  baseURL: API_ADMIN_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  }
});

// Intercepteur pour gérer les erreurs globalement
apiClient.interceptors.response.use(
  response => response,
  error => {
    console.error('API Error:', error.response?.data || error.message);
    return Promise.reject(error);
  }
);

// Intercepteur : ajoute le token Bearer à chaque requête admin
apiAdminClient.interceptors.request.use(
  config => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  error => Promise.reject(error)
);

apiAdminClient.interceptors.response.use(
  response => response,
  error => {
    if (error.response?.status === 401) {
      // Token expiré ou invalide → retour au login
      localStorage.removeItem('auth_token');
      window.location.href = '/login';
    }
    console.error('API Error:', error.response?.data || error.message);
    return Promise.reject(error);
  }
);

// ============================================
// 🔒 FONCTION DE PROTECTION DE LA VIE PRIVÉE
// ============================================

/**
 * Approxime les coordonnées GPS pour protéger la vie privée
 * Arrondit à une grille de ~50km (0.5 degrés)
 *
 * @param {number} latitude - Latitude exacte
 * @param {number} longitude - Longitude exacte
 * @returns {object} Coordonnées approximatives { latitude, longitude }
 *
 * Exemples:
 * - 33.5731, -7.5898 (Casablanca exact) → 33.5500, -7.6000
 * - 34.0209, -6.8416 (Rabat exact) → 34.0000, -6.8500
 *
 * Précision: ~50km (les visiteurs d'une même ville apparaissent au même point)
 */
const approximateCoordinates = (latitude, longitude) => {
  if (!latitude || !longitude) {
    return { latitude: null, longitude: null };
  }

  // Arrondir à 0.5 degrés (~50km)
  // 1 degré ≈ 111 km à l'équateur
  // 0.5 degrés ≈ 55 km
  const precision = 0.5;

  const approxLat = Math.round(latitude / precision) * precision;
  const approxLng = Math.round(longitude / precision) * precision;

  // Arrondir à 4 décimales pour éviter les problèmes de float
  return {
    latitude: Math.round(approxLat * 10000) / 10000,
    longitude: Math.round(approxLng * 10000) / 10000
  };
};

// ============================================
// SERVICES ARTICLES
// ============================================

export const ArticleService = {
  /**
   * Récupérer tous les articles avec filtres
   */
  getAll: async (params = {}) => {
    try {
      const response = await apiClient.get('/articles', { params });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Récupérer les 3 derniers articles publiés (pour la page d'accueil)
   */
  getLatestPublished: async () => {
    try {
      const response = await apiClient.get('/articles', {
        params: {
          status: 'published',
          per_page: 3,
          sort_by: 'publish_date',
          sort_order: 'desc'
        }
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Récupérer un article par ID
   */
  getById: async (id) => {
    try {
      const response = await apiClient.get(`/articles/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Créer un nouvel article
   */
  create: async (articleData) => {
    try {
      const response = await apiAdminClient.post('/articles', articleData);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Mettre à jour un article
   */
  update: async (id, articleData) => {
    try {
      const response = await apiAdminClient.put(`/articles/${id}`, articleData);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Supprimer un article
   */
  delete: async (id) => {
    try {
      const response = await apiAdminClient.delete(`/articles/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Récupérer les statistiques
   */
  getStats: async () => {
    try {
      const response = await apiClient.get('/articles/stats/dashboard');
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Récupérer les articles recommandés
   */
  getRecommended: async (id, limit = 3) => {
    try {
      const response = await apiClient.get(`/articles/${id}/recommended`, {
        params: { limit }
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }
};

// ============================================
// SERVICES AUTEURS
// ============================================

export const AuthorService = {
  getAll: async () => {
    try {
      const response = await apiClient.get('/authors');
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getById: async (id) => {
    try {
      const response = await apiClient.get(`/authors/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  create: async (authorData) => {
    try {
      const response = await apiAdminClient.post('/authors', authorData);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  update: async (id, authorData) => {
    try {
      const response = await apiAdminClient.put(`/authors/${id}`, authorData);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  delete: async (id) => {
    try {
      const response = await apiAdminClient.delete(`/authors/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }
};

// ============================================
// SERVICES CATÉGORIES
// ============================================

export const CategoryService = {
  getAll: async () => {
    try {
      const response = await apiClient.get('/categories');
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getById: async (id) => {
    try {
      const response = await apiClient.get(`/categories/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  create: async (categoryData) => {
    try {
      const response = await apiAdminClient.post('/categories', categoryData);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  update: async (id, categoryData) => {
    try {
      const response = await apiAdminClient.put(`/categories/${id}`, categoryData);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  delete: async (id) => {
    try {
      const response = await apiAdminClient.delete(`/categories/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }
};

// ============================================
// SERVICES LANGUES
// ============================================

export const LanguageService = {
  getAll: async () => {
    try {
      const response = await apiClient.get('/languages');
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getById: async (id) => {
    try {
      const response = await apiClient.get(`/languages/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }
};

// ============================================
// SERVICES TAGS
// ============================================

export const TagService = {
  getAll: async () => {
    try {
      const response = await apiClient.get('/tags');
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getById: async (id) => {
    try {
      const response = await apiClient.get(`/tags/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }
};

export const UserService = {
  /**
   * Récupérer tous les utilisateurs
   */
  getAll: async (params = {}) => {
    try {
      const response = await apiAdminClient.get('/users', { params });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Récupérer un utilisateur par ID
   */
  getById: async (id) => {
    try {
      const response = await apiAdminClient.get(`/users/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Créer un nouvel utilisateur
   */
  create: async (userData) => {
    try {
      const response = await apiAdminClient.post('/users', userData);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Mettre à jour un utilisateur
   */
  update: async (id, userData) => {
    try {
      const response = await apiAdminClient.put(`/users/${id}`, userData);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Changer le mot de passe d'un utilisateur
   */
  changePassword: async (id, passwordData) => {
    try {
      const response = await apiAdminClient.put(`/users/${id}/password`, passwordData);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Désactiver un utilisateur (soft delete)
   */
  deactivate: async (id) => {
    try {
      const response = await apiAdminClient.put(`/users/${id}/deactivate`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Activer un utilisateur
   */
  activate: async (id) => {
    try {
      const response = await apiAdminClient.put(`/users/${id}/activate`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Récupérer les statistiques d'un utilisateur
   */
  getStats: async (id) => {
    try {
      const response = await apiAdminClient.get(`/users/${id}/stats`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Récupérer les articles d'un utilisateur
   */
  getArticles: async (id, params = {}) => {
    try {
      const response = await apiAdminClient.get(`/users/${id}/articles`, { params });
      return response.data;
    } catch (error) {
      throw error;
    }
  }
};

export const AnalyticsService = {
  /**
   * Envoyer un événement analytics
   * 🔒 SÉCURISÉ : Les coordonnées GPS sont approximées avant l'envoi
   */
  track: async (eventData) => {
    try {
      // 🔒 Approximer les coordonnées pour protéger la vie privée
      if (eventData.latitude && eventData.longitude) {
        const approxCoords = approximateCoordinates(
          eventData.latitude,
          eventData.longitude
        );

        // Remplacer par les coordonnées approximatives
        eventData.latitude = approxCoords.latitude;
        eventData.longitude = approxCoords.longitude;

        // Log pour debug (optionnel)
        console.log('🔒 Coordonnées approximées pour la vie privée (~50km)');
      }

      const response = await apiClient.post('/analytics/track', eventData);
      return response.data;
    } catch (error) {
      // En cas d'erreur, on log mais on ne bloque pas l'expérience utilisateur
      console.warn('Analytics tracking failed:', error);
      return null;
    }
  },

  /**
   * Récupérer les statistiques globales
   */
  getStats: async (days = 30) => {
    try {
      const response = await apiAdminClient.get('/analytics/stats', {
        params: { days }
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Récupérer les articles populaires
   */
  getPopularArticles: async (days = 30, limit = 10) => {
    try {
      const response = await apiAdminClient.get('/analytics/popular-articles', {
        params: { days, limit }
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Récupérer les statistiques par pays
   */
  getStatsByCountry: async (days = 30) => {
    try {
      const response = await apiAdminClient.get('/analytics/by-country', {
        params: { days }
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Récupérer les statistiques temporelles
   */
  getStatsByTime: async (days = 30) => {
    try {
      const response = await apiAdminClient.get('/analytics/by-time', {
        params: { days }
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Récupérer les stats d'un article spécifique
   */
  getArticleStats: async (articleId, days = 30) => {
    try {
      const response = await apiAdminClient.get(`/analytics/article/${articleId}`, {
        params: { days }
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Récupérer les points de géolocalisation
   */
  getLocationPoints: async (days = 30) => {
    try {
      const response = await apiAdminClient.get('/analytics/location-points', {
        params: { days }
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },
};



// Mettre à jour l'export par défaut
export default {
  ArticleService,
  AuthorService,
  CategoryService,
  LanguageService,
  TagService,
  UserService,
  AnalyticsService
};
